import { Fragment, useEffect, useMemo, useState } from 'react';
import { api } from '../../api.js';
import { Spinner, ErrorNote, TaskStatusBadge, TaskPriorityBadge, fmtDay } from '../../components.jsx';

const COLUMNS = [
  { id: 'jobNumber', label: 'Job #' },
  { id: 'opportunityName', label: 'Opportunity' },
  { id: 'taskType', label: 'Type' },
  { id: 'status', label: 'Status' },
  { id: 'priority', label: 'Priority' },
  { id: 'name', label: 'Task' },
  { id: 'dueDate', label: 'Due' },
];

const PRIORITY_ORDER = { Urgent: 0, High: 1, Normal: 2, Low: 3 };

function sortValue(task, col) {
  if (col === 'dueDate') return task.dueDate ? new Date(task.dueDate).getTime() : Infinity;
  if (col === 'priority') return PRIORITY_ORDER[task.priority] ?? 4;
  return (task[col] || '').toString().toLowerCase();
}

// A dueDate carries a time, but the display only ever shows the day -- so
// "overdue" is judged against local midnight today, not the exact instant.
// Otherwise something due later today would flash red before its actual
// deadline has even passed, which would just be confusing.
function isOverdue(dueDate) {
  if (!dueDate) return false;
  const todayMidnight = new Date(new Date().toDateString());
  return new Date(dueDate) < todayMidnight;
}

function taskTypes(task) {
  return task.taskType ? task.taskType.split(', ') : [];
}

export default function SalesTodoWidget() {
  const [users, setUsers] = useState(null);
  const [asUser, setAsUser] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ col: 'dueDate', dir: 'asc' });
  const [typeFilter, setTypeFilter] = useState('');
  const [openNoteId, setOpenNoteId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    api.get('/api/widgets/sales-todo/users').then(setUsers).catch(() => {}); // dropdown just won't offer switching if this fails
  }, []);

  const load = () => {
    const qs = asUser ? `?asUser=${encodeURIComponent(asUser)}` : '';
    return api.get(`/api/widgets/sales-todo${qs}`).then(setData).catch((e) => setError(e.message));
  };

  useEffect(() => {
    setData(null);
    setError(null);
    setTypeFilter(''); // switching user shouldn't leave a stale filter silently hiding everything
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asUser]);

  // Built from the full unfiltered list, so choosing a type doesn't make the
  // other options disappear from the dropdown.
  const typeOptions = useMemo(() => {
    if (!data) return [];
    const set = new Set();
    data.tasks.forEach((t) => taskTypes(t).forEach((ty) => set.add(ty)));
    return [...set].sort();
  }, [data]);

  const sorted = useMemo(() => {
    if (!data) return [];
    const items = data.tasks.filter((t) => !typeFilter || taskTypes(t).includes(typeFilter));
    items.sort((a, b) => {
      const av = sortValue(a, sort.col);
      const bv = sortValue(b, sort.col);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [data, sort, typeFilter]);

  const toggleSort = (col) =>
    setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));

  const isSelf = data?.viewedUser?.isSelf;

  const complete = async (task) => {
    setBusyId(task.id);
    setData((d) => ({ ...d, tasks: d.tasks.filter((t) => t.id !== task.id) })); // optimistic
    try {
      await api.post(`/api/widgets/sales-todo/tasks/${task.id}/complete`);
    } catch (e) {
      setError(e.message);
      load(); // roll back on failure
    } finally {
      setBusyId(null);
    }
  };

  const postNote = async (task) => {
    if (!noteText.trim()) return;
    setBusyId(task.id);
    try {
      await api.post(`/api/widgets/sales-todo/tasks/${task.id}/note`, { text: noteText.trim() });
      setNoteText('');
      setOpenNoteId(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const toggleNote = (task) => {
    setOpenNoteId(openNoteId === task.id ? null : task.id);
    setNoteText('');
  };

  const noteBox = (task) => (
    <div className="flex gap-2">
      <input
        className="input !py-1 text-xs"
        placeholder="Post a note to this task's Espo stream…"
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && postNote(task)}
      />
      <button className="btn-primary shrink-0 !px-3 !py-1 text-xs" disabled={busyId === task.id} onClick={() => postNote(task)}>
        Post
      </button>
    </div>
  );

  return (
    <div className="card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
        {typeOptions.length > 0 && (
          <select className="input !w-auto !py-1 text-xs" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {typeOptions.map((ty) => (
              <option key={ty} value={ty}>
                {ty}
              </option>
            ))}
          </select>
        )}
        {users && (
          <select
            className="input !w-auto !py-1 text-xs"
            value={asUser}
            onChange={(e) => setAsUser(e.target.value)}
          >
            <option value="">Me</option>
            {users.map((u) => (
              <option key={u.sub} value={u.sub}>
                {u.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {!data && !error && <Spinner label="Loading tasks…" />}

      {data && !data.espoLinked && (
        <p className="text-sm text-steel">No EspoCRM account linked{isSelf ? ' to your login' : ' for this person'}.</p>
      )}

      {data && data.espoLinked && sorted.length === 0 && (
        <p className="text-sm text-steel">
          {data.tasks.length > 0
            ? 'No tasks match that filter.'
            : isSelf
            ? "You're all caught up."
            : `${data.viewedUser.name} is all caught up.`}
        </p>
      )}

      {data && sorted.length > 0 && (
        <>
          {/* Desktop: a sortable table. Below the md breakpoint this hides in
              favour of the stacked card layout below, which lets long task/
              opportunity names wrap onto multiple lines instead of truncating
              or forcing horizontal scroll on a phone. */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-steel/10 text-xs uppercase tracking-wide text-steel">
                  {COLUMNS.map((c) => (
                    <th
                      key={c.id}
                      className="cursor-pointer select-none whitespace-nowrap py-1.5 pr-3 font-bold"
                      onClick={() => toggleSort(c.id)}
                    >
                      {c.label}
                      {sort.col === c.id ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                  <th className="py-1.5 pr-3"></th>
                  <th className="w-8 py-1.5"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-steel/10">
                {sorted.map((task) => (
                  <Fragment key={task.id}>
                    <tr>
                      <td className="whitespace-nowrap py-2 pr-3">
                        {task.jobNumber ? (
                          <a href={task.opportunityUrl} target="_blank" rel="noreferrer" className="hover:underline">
                            {task.jobNumber}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="max-w-[10rem] truncate py-2 pr-3">
                        {task.opportunityName ? (
                          <a href={task.opportunityUrl} target="_blank" rel="noreferrer" className="hover:underline">
                            {task.opportunityName}
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3 text-xs text-steel">{task.taskType || '—'}</td>
                      <td className="whitespace-nowrap py-2 pr-3">
                        <TaskStatusBadge status={task.status} />
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3">
                        <TaskPriorityBadge priority={task.priority} />
                      </td>
                      <td className="max-w-[12rem] truncate py-2 pr-3">
                        <a href={task.url} target="_blank" rel="noreferrer" className="hover:underline">
                          {task.name}
                        </a>
                      </td>
                      <td className={`whitespace-nowrap py-2 pr-3 text-xs ${isOverdue(task.dueDate) ? 'font-semibold text-ascred' : 'text-steel'}`}>
                        {fmtDay(task.dueDate)}
                      </td>
                      <td className="whitespace-nowrap py-2 pr-3">
                        <button
                          className="text-xs text-steel underline disabled:no-underline disabled:opacity-40"
                          disabled={!isSelf}
                          onClick={() => toggleNote(task)}
                        >
                          Note
                        </button>
                      </td>
                      <td className="py-2">
                        <input type="checkbox" disabled={!isSelf || busyId === task.id} onChange={() => complete(task)} />
                      </td>
                    </tr>
                    {openNoteId === task.id && (
                      <tr>
                        <td colSpan={COLUMNS.length + 2} className="pb-2">
                          {noteBox(task)}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: one stacked card per task, text wraps naturally. */}
          <div className="space-y-3 md:hidden">
            {sorted.map((task) => (
              <div key={task.id} className="rounded-lg border border-steel/10 p-3 text-sm">
                <div className="mb-1 flex items-start justify-between gap-2">
                  <a href={task.url} target="_blank" rel="noreferrer" className="font-medium hover:underline">
                    {task.name}
                  </a>
                  <input
                    type="checkbox"
                    className="mt-1 shrink-0"
                    disabled={!isSelf || busyId === task.id}
                    onChange={() => complete(task)}
                  />
                </div>
                <div className="mb-2 text-xs text-steel">
                  {task.jobNumber && (
                    <>
                      <a href={task.opportunityUrl} target="_blank" rel="noreferrer" className="hover:underline">
                        {task.jobNumber}
                      </a>
                      {' · '}
                    </>
                  )}
                  {task.opportunityName ? (
                    <a href={task.opportunityUrl} target="_blank" rel="noreferrer" className="hover:underline">
                      {task.opportunityName}
                    </a>
                  ) : (
                    '—'
                  )}
                </div>
                <div className="mb-2 flex flex-wrap items-center gap-1.5">
                  <TaskStatusBadge status={task.status} />
                  <TaskPriorityBadge priority={task.priority} />
                  {task.taskType && <span className="badge bg-steel/10 text-steel">{task.taskType}</span>}
                </div>
                <div className={`mb-2 text-xs ${isOverdue(task.dueDate) ? 'font-semibold text-ascred' : 'text-steel'}`}>
                  Due {fmtDay(task.dueDate)}
                </div>
                <button
                  className="text-xs text-steel underline disabled:no-underline disabled:opacity-40"
                  disabled={!isSelf}
                  onClick={() => toggleNote(task)}
                >
                  Note
                </button>
                {openNoteId === task.id && <div className="mt-2">{noteBox(task)}</div>}
              </div>
            ))}
          </div>
        </>
      )}

      {data && data.zammadLinked && data.zammadTickets.length > 0 && (
        <div className="mt-4 border-t border-steel/10 pt-3">
          <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-steel">New Zammad cases</h3>
          <ul className="divide-y divide-steel/10">
            {data.zammadTickets.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-2 py-1.5 text-sm">
                <a href={t.url} target="_blank" rel="noreferrer" className="min-w-0 truncate hover:underline">
                  #{t.number} — {t.title}
                </a>
                <span className="shrink-0 text-xs text-steel">{fmtDay(t.createdAt)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
